import { Component, inject, signal } from '@angular/core';
import { SectionHeaderComponent } from '@apps/landing/src/app/shared/components/section-header/section-header.component';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { SharedModule } from '@apps/landing/src/app/shared/shared.module';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { InfoCardComponent } from '@apps/landing/src/app/shared/components/cards/info-card/info-card.component';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, Subject, takeUntil } from 'rxjs';
import { HlmSkeletonComponent } from '@spartan-ng/ui-skeleton-helm';
import { HlmAccordionImports } from '@spartan-ng/helm/accordion';
import { OurServicesService } from '@apps/landing/src/app/modules/services/services/our-services.service';
import { IServiceModel } from '@apps/landing/src/app/modules/services/models/service.model';
import { handleResponseMessage, RESPONSE_TYPE } from '@apps/landing/src/app/shared/utils/handle-response-message';
import { FormsModule } from '@angular/forms';
import { PAGE_ROUTES } from '@apps/landing/src/app/shared/constants/routes.enum';
import { faq, particularities, projects } from '@apps/landing/src/app/modules/services/utils/constants';
import { PAGINATION_CONSTANTS } from '@apps/landing/src/app/shared/constants/pagination-constants';
import { Unsubscribe } from '@apps/landing/src/app/shared/classes/unsubscribe';

@Component({
  selector: 'app-services',
  imports: [
    SectionHeaderComponent,
    HlmInputDirective,
    SharedModule,
    HlmButtonDirective,
    InfoCardComponent,
    HlmSkeletonComponent,
    HlmAccordionImports,
    FormsModule,
  ],
  standalone: true,
  templateUrl: './services.component.html',
})
export class ServicesComponent extends Unsubscribe {
  private readonly router = inject(Router);
  private readonly ourServicesService = inject(OurServicesService);

  private readonly handleResponseMessage = handleResponseMessage();
  protected readonly projects = projects;
  protected readonly particularities = particularities;
  protected readonly faq = faq;

  services = signal<IServiceModel[]>([]);
  loading = signal<boolean>(false);

  protected searchTerm: string = '';
  private readonly searchSubject = new Subject<string>();
  private readonly possibleWidths = [
    'w-[180px]',
    'w-[200px]',
    'w-[220px]',
    'w-[240px]',
    'w-[260px]',
    'w-[280px]',
    'w-[300px]',
    'w-[320px]',
  ];

  skeletonPlaceholders = Array.from({ length: 15 }).map(() => ({
    widthClass: this.possibleWidths[Math.floor(Math.random() * this.possibleWidths.length)],
  }));

  ngOnInit() {
    this.getServices();
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(term => this.filterBadges(term));
  }

  private getServices(searchTerm?: string) {
    this.loading.set(true);

    this.ourServicesService
      .getServices(0, PAGINATION_CONSTANTS.MAX_LENGTH, searchTerm)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: response => {
          if (response.status && response.data.data) {
            this.services.set(response.data.data);
          } else {
            this.handleResponseMessage('Something went wrong. Please try again.', RESPONSE_TYPE.ERROR);
          }
        },
      });
  }

  protected onSearch(term: string) {
    this.loading.set(true);
    this.searchSubject.next(term);
  }

  protected filterBadges(term: string) {
    const lowerTerm = term.toLowerCase().trim();
    this.getServices(lowerTerm);
  }

  protected goToProject(data: any) {
    this.router.navigate([PAGE_ROUTES.SERVICES, data.projectId]);
  }

  protected clearSearch(): void {
    this.searchTerm = '';
    this.getServices();
  }
}
